import * as OrganizationDocumentsActions from "./organization-document.actions";

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {OrganizationDocument, OrganizationDocumentSearchParams} from '@documents/_common/models/organization-document.models';
import {CategoryListResponse} from '@common/components-standalone/reference-category-module/category.type';
import {CategoriesTreeState, CategorizedEntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const organizationDocumentFeatureKey = 'organizationDocuments';

let currentSortType: string = 'DATE_DESC';

export function sortEntities(a: OrganizationDocument, b: OrganizationDocument): number {
    const currentSort = currentSortType || 'DATE_DESC';

    switch (currentSort) {
        case 'NAME_ASC':
            return a.name!.localeCompare(b.name!);
        case 'NAME_DESC':
            return b.name!.localeCompare(a.name!);
        case 'DATE_ASC':
            return new Date(a.date!).getTime() - new Date(b.date!).getTime();
        case 'DATE_DESC':
            return new Date(b.date!).getTime() - new Date(a.date!).getTime();
        case 'CUSTOM_DATE_ASC':
            return new Date(a.customDate!).getTime() - new Date(b.customDate!).getTime();
        case 'CUSTOM_DATE_DESC':
            return new Date(b.customDate!).getTime() - new Date(a.customDate!).getTime();
        default:
            return 0;
    }
}

export function sortCategories(a: CategoryListResponse, b: CategoryListResponse) {
    return a.category.name.localeCompare(b.category.name);
}

export const adapter: EntityAdapter<OrganizationDocument> = createEntityAdapter<OrganizationDocument>({
    sortComparer: sortEntities
});

export const categoriesAdapter: EntityAdapter<CategoryListResponse> = createEntityAdapter<CategoryListResponse>({
    selectId: (category) => category.category.id!,
    sortComparer: sortCategories
});

export type OrganizationDocumentState = CategorizedEntityStateModel<OrganizationDocument, OrganizationDocumentSearchParams>;

export const initialState: OrganizationDocumentState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: [],
    entityCategories: categoriesAdapter.getInitialState(),
    categoriesTreeState: {
        opened: {'default': true}
    },
    filter: {
        sort: 'DATE_DESC'
    }
});

export const organizationDocumentReducer = createReducer(
    initialState,

    on(OrganizationDocumentsActions.SetCategoriesState, (state, action) => ({
        ...state,
        categoriesTreeState: action.payload
    })),

    on(OrganizationDocumentsActions.LoadCategoriesSuccess, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.setAll(action.payload, {...state.entityCategories})
    })),

    on(OrganizationDocumentsActions.UpsertCategory, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.setOne(action.payload, {...state.entityCategories})
    })),

    on(OrganizationDocumentsActions.DeleteCategory, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.removeOne(action.payload.id!, {...state.entityCategories})
    })),

    on(OrganizationDocumentsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                savedCategoriesTreeState: !action.payload.close && state.categoriesTreeState ? state.categoriesTreeState : initialState.categoriesTreeState,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined,
                categoriesTreeState: action.payload.close && state.savedCategoriesTreeState ? state.savedCategoriesTreeState : initialState.categoriesTreeState
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(OrganizationDocumentsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(OrganizationDocumentsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(OrganizationDocumentsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(OrganizationDocumentsActions.SetFilter, (state, action) => {
        currentSortType = action.payload.sort || 'DATE_DESC';
        return {
            ...state,
            filter: action.payload
        };
    }),

    on(OrganizationDocumentsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(OrganizationDocumentsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(OrganizationDocumentsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(OrganizationDocumentsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload, state?.categoriesTreeState);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(OrganizationDocumentsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(OrganizationDocumentsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(OrganizationDocumentsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(OrganizationDocumentsActions.UnbindDocument, (state, action) => {
        if (action.payload.deleteDocument) {
            return adapter.removeOne(action.payload.attachmentId, state);
        }

        const attachedTo = state.entities[action.payload.attachmentId]?.attachedTo?.filter(el => el.id !== action.payload.request.itemId);
        return adapter.updateOne({
            changes: {
                attachedTo: attachedTo
            },
            id: action.payload.attachmentId
        }, state);
    }),

    on(OrganizationDocumentsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();
export const {selectAll: selectAllCategories} = categoriesAdapter.getSelectors((state: OrganizationDocumentState) => state.entityCategories);

const isAvailableByFilter = (searchParams: OrganizationDocumentSearchParams | undefined, item: OrganizationDocument, categoriesTreeState?: CategoriesTreeState): boolean => {
    if (categoriesTreeState?.selectedCategory?.id && categoriesTreeState.selectedCategory?.id != item.category?.id) {
        return false;
    }

    if (!searchParams) {
        return true;
    }

    if (searchParams.query && item.name && item.name.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1) {
        return false;
    }

    return true;
};
